'use client';

import { useState } from 'react';
import {
  Upload,
  Zap,
  RefreshCw,
  FileSpreadsheet,
  Sparkles,
  Eye,
  ArrowDownToLine,
  Filter,
  Settings,
  Play,
  Undo2,
  Calendar,
  Clock,
  Repeat,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// =============================================================================
// STEP DATA
// =============================================================================

interface WalkthroughStep {
  badge: { icon: LucideIcon; label: string; color: string };
  heading: string;
  subtitle: string;
  workflow: { icon: LucideIcon; label: string }[];
  details: string[];
  tier: string;
}

const STEPS: WalkthroughStep[] = [
  {
    badge: { icon: Upload, label: 'CSV / EXCEL IMPORT', color: 'bg-blue-100 text-blue-700' },
    heading: 'Import any file safely',
    subtitle: 'Upload CSV or Excel files and let AI map your columns automatically.',
    workflow: [
      { icon: FileSpreadsheet, label: 'Upload' },
      { icon: Sparkles, label: 'AI mapping' },
      { icon: Eye, label: 'Preview' },
      { icon: ArrowDownToLine, label: 'Import' },
    ],
    details: [
      'AI auto-maps CSV columns to your board columns — override anything manually.',
      'Preview every row before it touches your board. Nothing runs without your confirmation.',
      'Rollback available after import — undo in one click if something looks wrong.',
    ],
    tier: 'Free and unlimited',
  },
  {
    badge: { icon: Zap, label: 'BULK OPERATIONS', color: 'bg-purple-100 text-purple-700' },
    heading: 'Update hundreds of items at once',
    subtitle: 'Filter, configure, preview, and execute bulk changes with safety built in.',
    workflow: [
      { icon: Filter, label: 'Filter' },
      { icon: Settings, label: 'Configure' },
      { icon: Eye, label: 'Preview' },
      { icon: Play, label: 'Execute' },
    ],
    details: [
      'Filter by any column, status, or date — then update or delete items in bulk.',
      'See exactly which items will change and what the new values will be before running.',
      'One-click rollback within 24 hours (48h on Pro) if anything goes wrong.',
    ],
    tier: '5 free per month',
  },
  {
    badge: { icon: RefreshCw, label: 'RECURRING TASKS', color: 'bg-amber-100 text-amber-700' },
    heading: 'Automate repeating work',
    subtitle: 'Create items on a schedule so your board is always ready for the next sprint.',
    workflow: [
      { icon: Calendar, label: 'Schedule' },
      { icon: Settings, label: 'Template' },
      { icon: Clock, label: 'Runs' },
      { icon: CheckCircle, label: 'Created' },
    ],
    details: [
      'Set daily, weekly, monthly, or custom schedules — runs automatically in the background.',
      'Define a template once: name, group, column values. Every execution creates the items fresh.',
      'Execution history shows every run — what was created, when, and if anything failed.',
    ],
    tier: '10 free per month',
  },
];

// =============================================================================
// COMPONENT
// =============================================================================

interface WalkthroughScreenProps {
  onComplete: () => void;
  onSkip: () => void;
}

export function WalkthroughScreen({ onComplete, onSkip }: WalkthroughScreenProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const step = STEPS[currentStep] as WalkthroughStep;
  const isLast = currentStep === STEPS.length - 1;
  const BadgeIcon = step.badge.icon;

  return (
    <div className="min-h-screen flex flex-col bg-[var(--surface-bg)]">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-default)]">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <img src="/taskloops-logo.png" alt="TaskLoops" className="w-7 h-7 rounded-lg" />
          <span className="font-heading text-sm font-semibold text-[var(--text-heading)]">TaskLoops</span>
        </div>

        {/* Progress Indicators */}
        <div className="flex items-center gap-1.5">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1 w-6 rounded-full transition-colors ${
                i <= currentStep ? 'bg-primary-600' : 'bg-[var(--surface-skeleton)]'
              }`}
            />
          ))}
        </div>

        {/* Skip */}
        <button
          onClick={onSkip}
          className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-body)] transition-colors"
        >
          Skip to app
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="max-w-lg w-full">
          {/* Feature Badge */}
          <div className="flex justify-center mb-6">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${step.badge.color}`}>
              <BadgeIcon className="w-3.5 h-3.5" />
              {step.badge.label}
            </span>
          </div>

          {/* Heading */}
          <h2 className="font-heading text-2xl font-bold text-[var(--text-heading)] text-center mb-2">
            {step.heading}
          </h2>
          <p className="text-sm text-[var(--text-secondary)] text-center mb-8">
            {step.subtitle}
          </p>

          {/* Workflow Icons */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {step.workflow.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={item.label} className="flex items-center gap-2">
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-lg bg-[var(--surface-card)] border border-[var(--border-default)] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[var(--text-secondary)]" />
                    </div>
                    <span className="text-[10px] text-[var(--text-muted)]">{item.label}</span>
                  </div>
                  {i < step.workflow.length - 1 && (
                    <ChevronRight className="w-4 h-4 text-[var(--text-muted)] mb-4" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Key Details */}
          <div className="space-y-3 mb-6">
            {step.details.map((detail, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold">
                  {i + 1}
                </span>
                <p className="text-sm text-[var(--text-body)]">{detail}</p>
              </div>
            ))}
          </div>

          {/* Tier Info */}
          <p className="text-xs text-[var(--text-muted)] text-center">
            {step.tier}
          </p>
        </div>
      </div>

      {/* Bottom Nav */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-[var(--border-default)]">
        <button
          onClick={() => setCurrentStep(currentStep - 1)}
          disabled={currentStep === 0}
          className="flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-body)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>

        <span className="text-sm text-[var(--text-muted)]">
          {currentStep + 1} of {STEPS.length}
        </span>

        <button
          onClick={isLast ? onComplete : () => setCurrentStep(currentStep + 1)}
          className="flex items-center gap-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
        >
          {isLast ? 'Go to app' : 'Next'}
          {!isLast && <ChevronRight className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

